import { COMING_SOON_ENABLED, comingSoonRoutes } from './config';

const normalizePath = (pathname: string): string => {
  if (!pathname) return '/';
  const path = pathname.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
};

const splitPath = (path: string): string[] => path.split('/').filter(Boolean);

const segmentMatches = (patternSegment: string, pathSegment: string | undefined): boolean => {
  if (patternSegment.startsWith(':')) {
    return Boolean(pathSegment?.length);
  }
  return patternSegment === pathSegment;
};

/**
 * Match a configured pattern against the current pathname.
 */
export const matchComingSoonPattern = (pattern: string, pathname: string): boolean => {
  const path = normalizePath(pathname);
  const normalizedPattern = normalizePath(pattern);

  if (normalizedPattern.endsWith('/*')) {
    const basePattern = normalizedPattern.slice(0, -2);
    const baseParts = splitPath(basePattern);
    const pathParts = splitPath(path);

    if (baseParts.length === 0) {
      return true;
    }

    if (pathParts.length < baseParts.length) {
      return false;
    }

    const baseMatches = baseParts.every((segment, index) =>
      segmentMatches(segment, pathParts[index])
    );

    if (!baseMatches) {
      return false;
    }

    // Literal prefix (e.g. /prompts/*): include the base path itself
    const isLiteralPrefix = baseParts.every((segment) => !segment.startsWith(':'));
    if (isLiteralPrefix) {
      return path === basePattern || path.startsWith(`${basePattern}/`);
    }

    // Dynamic prefix (e.g. /agent-details/:agentId/*): sub-routes only
    return pathParts.length > baseParts.length;
  }

  const patternParts = splitPath(normalizedPattern);
  const pathParts = splitPath(path);

  if (patternParts.length !== pathParts.length) {
    return false;
  }

  return patternParts.every((segment, index) => segmentMatches(segment, pathParts[index]));
};

/** True when the pathname is restricted to the Coming Soon page. */
export const isComingSoonRoute = (pathname: string): boolean => {
  if (!COMING_SOON_ENABLED || comingSoonRoutes.length === 0) {
    return false;
  }

  const path = normalizePath(pathname);
  return comingSoonRoutes.some((pattern) => matchComingSoonPattern(pattern, path));
};

/** Resolve the matching config pattern for the current path (useful for labels/debug). */
export const getComingSoonPattern = (pathname: string): string | undefined => {
  if (!COMING_SOON_ENABLED) {
    return undefined;
  }

  const path = normalizePath(pathname);
  return comingSoonRoutes.find((pattern) => matchComingSoonPattern(pattern, path));
};
